import React, { useEffect, useState } from "react";
import { Plus, Pencil, KeyRound, Trash2, Loader2, ShieldCheck, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const ROLES = ["member", "admin"];
const TIERS = ["explorer", "member", "circle_leader"];
const STATUSES = ["active", "pending", "cancelled"];

const emptyForm = { name: "", email: "", password: "", role: "member", tier: "explorer", membership_status: "active" };

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(emptyForm);

  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");

  const [delUser, setDelUser] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => toast.error("Could not load users")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setEditOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email || "", password: "", role: u.role || "member", tier: u.tier || "explorer", membership_status: u.membership_status || "active" });
    setEditOpen(true);
  };

  const saveUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/users/${editing.id}`, {
          name: form.name, email: form.email, role: form.role, tier: form.tier, membership_status: form.membership_status,
        });
        toast.success("User updated");
      } else {
        const { data } = await api.post("/admin/users", form);
        const em = data?.welcome_email;
        if (em?.sent) toast.success("User created — welcome email sent");
        else toast.success(`User created${em?.reason ? ` (email not sent: ${em.reason})` : ""}`);
      }
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save user");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${pwUser.id}/reset-password`, { password: newPw });
      toast.success(`Password reset for ${pwUser.email}`);
      setPwOpen(false); setNewPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${delUser.id}`);
      toast.success("User deleted");
      setDelUser(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete user");
    } finally {
      setBusy(false);
    }
  };

  const filtered = users.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [u.name, u.email, u.role, u.tier, u.membership_status].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [query]);

  const roleBadge = (role) => role === "admin"
    ? "bg-secondary/15 text-secondary"
    : "bg-primary/10 text-primary";
  const statusBadge = (s) => s === "active"
    ? "bg-emerald-100 text-emerald-700"
    : s === "pending" ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground";

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-semibold text-primary mb-1">Users</h1>
          <p className="text-muted-foreground">{users.length} account{users.length === 1 ? "" : "s"} · manage members and admins.</p>
        </div>
        <Button data-testid="user-add-btn" onClick={openCreate} className="rounded-full">
          <Plus size={16} className="mr-2" /> Add user
        </Button>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="user-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, role or tier…" className="pl-10 rounded-full" />
      </div>

      {loading ? (
        <div className="min-h-[30vh] flex items-center justify-center font-display text-3xl text-primary/40 animate-pulse">GYBC</div>
      ) : (
        <div className="bg-card border border-primary/10 rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-primary/10">
                  <th className="px-5 py-4 font-semibold">Name</th>
                  <th className="px-5 py-4 font-semibold">Email</th>
                  <th className="px-5 py-4 font-semibold">Role</th>
                  <th className="px-5 py-4 font-semibold">Tier</th>
                  <th className="px-5 py-4 font-semibold">Status</th>
                  <th className="px-5 py-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-primary/5 last:border-0 hover:bg-primary/[0.02]">
                    <td className="px-5 py-4 font-medium text-primary whitespace-nowrap flex items-center gap-2">
                      {u.role === "admin" && <ShieldCheck size={15} className="text-secondary" />}
                      {u.name}
                      {me?.id === u.id && <span className="text-[11px] text-muted-foreground">(you)</span>}
                    </td>
                    <td className="px-5 py-4 text-foreground/80">{u.email}</td>
                    <td className="px-5 py-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${roleBadge(u.role)}`}>{u.role}</span></td>
                    <td className="px-5 py-4 capitalize text-foreground/80">{(u.tier || "—").replace("_", " ")}</td>
                    <td className="px-5 py-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusBadge(u.membership_status)}`}>{u.membership_status || "—"}</span></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} title="Edit" className="w-9 h-9 rounded-full flex items-center justify-center text-primary/70 hover:bg-primary/10 transition-colors"><Pencil size={16} /></button>
                        <button data-testid={`user-resetpw-${u.id}`} onClick={() => { setPwUser(u); setNewPw(""); setPwOpen(true); }} title="Reset password" className="w-9 h-9 rounded-full flex items-center justify-center text-primary/70 hover:bg-primary/10 transition-colors"><KeyRound size={16} /></button>
                        <button data-testid={`user-delete-${u.id}`} onClick={() => setDelUser(u)} disabled={me?.id === u.id} title={me?.id === u.id ? "You can't delete yourself" : "Delete"} className="w-9 h-9 rounded-full flex items-center justify-center text-red-500/80 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {paged.length === 0 && (
            <p data-testid="user-empty" className="px-5 py-10 text-center text-muted-foreground">No users match "{query}".</p>
          )}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-t border-primary/10 text-sm">
              <span className="text-muted-foreground">Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
              <div className="flex items-center gap-2">
                <button data-testid="user-page-prev" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-primary/15 text-primary hover:bg-primary/5 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
                <span className="text-primary font-medium">{safePage} / {totalPages}</span>
                <button data-testid="user-page-next" disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="w-9 h-9 rounded-full flex items-center justify-center border border-primary/15 text-primary hover:bg-primary/5 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="user-form-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle>
            <DialogDescription>{editing ? "Update this account's details and access." : "Create a new member or admin account."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveUser} className="space-y-4">
            <Field label="Full name">
              <Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </Field>
            <Field label="Email">
              <Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </Field>
            {!editing && (
              <Field label="Temporary password">
                <Input data-testid="user-form-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 8 characters" required minLength={8} />
              </Field>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Role">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={editing && me?.id === editing.id}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Membership tier">
                <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
                  <SelectTrigger data-testid="user-form-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIERS.map((tr) => <SelectItem key={tr} value={tr} className="capitalize">{tr.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Membership status">
              <Select value={form.membership_status} onValueChange={(v) => setForm({ ...form, membership_status: v })} disabled={editing && me?.id === editing.id}>
                <SelectTrigger data-testid="user-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
              {editing && me?.id === editing.id && (
                <p data-testid="self-edit-hint" className="text-xs text-muted-foreground mt-1.5">You can't change your own role or disable your own account.</p>
              )}
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="user-form-save" disabled={busy} className="rounded-full">
                {busy && <Loader2 size={16} className="animate-spin mr-2" />}{editing ? "Save changes" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="user-resetpw-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for {pwUser?.email}.</DialogDescription>
          </DialogHeader>
          <Field label="New password">
            <Input data-testid="user-resetpw-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min. 8 characters" />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button data-testid="user-resetpw-save" disabled={busy} onClick={resetPassword} className="rounded-full">
              {busy && <Loader2 size={16} className="animate-spin mr-2" />}Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-semibold">{delUser?.name}</span> ({delUser?.email}). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" onClick={deleteUser} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
